import React, { useCallback, useEffect, useState } from "react";
import { CiUnlock, CiLock } from "react-icons/ci";
import { useNavigate } from "react-router-dom";
import { getAllPendingProspects, getMerchants, migrateMerchants } from "../../services/merchant";
import UpdateProspectModal from "./UpdateProspectModal";

import toast from "react-hot-toast";
import Pagination from "../../components/Pagination";
import ProfileNav from "../../components/ProfileNav";
import Spinner from "../../components/Spinner";
import DataTableBase from "../../components/Datatable";
import { useAuth } from "../../context/AuthContextProvider";

const FilterComponent = ({ filterText, onFilterName, onClear }) => (
  <div style={{ display: "flex", alignItems: "center" }}>
    <input
      type="text"
      id="search"
      placeholder="Search Merchant"
      aria-label="Search Input"
      value={filterText}
      onChange={onFilterName}
      className="focus:outline-none rounded-sm pr-[32px] pl-[16px] h-[48px] border border-[#e5e5e5]"
    />
    <button
      onClick={onClear}
      type="button"
      className={`cursor-pointer  text-white rounded-lg py-3 px-6 bg-blue-500 rounded-l-sm`}
    >
      X
    </button>
  </div>
);

const Merchant = () => {
  const {userrole, user} = useAuth();
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Number of items per page
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resetPaginationToggle, setResetPaginationToggle] =
    React.useState(false);
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("merchants"); // "merchants" or "pending"
  const [pendingProspects, setPendingProspects] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [pendingSearchTerm, setPendingSearchTerm] = useState("");

  const fetchMerchants = async () => {
    try {
      const res = await getMerchants();
      setUsers(res.data);
      setFilteredUsers(res.data);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
      }
    }
  };

  const handleMerchantInfo = (id) => {
    navigate(`/merchants/${id}`);
  };

  const migrateUnregisteredMerchants = async () => {
    setLoading(true);
    try {
      const res = await migrateMerchants();
      toast.success(res.message);
      setLoading(false);
    } catch (error) {
      if (error.response) {
        setLoading(false);
        toast.error(error.response.data.message);
      }
    }
  };

  const fetchPendingRequests = async () => {
    setPendingLoading(true);
    try {
      const res = await getAllPendingProspects({
        PageNumber: 1,
        PageSize: 100, // Fetch more for now as we have a simple filter
      });
      setPendingProspects(res.data);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message);
      }
    } finally {
      setPendingLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "merchants") {
      fetchMerchants();
    } else {
      fetchPendingRequests();
    }
  }, [activeTab]);

  const columns = [
    {
      name: "Merchant ID",
      selector: (row) => row.id,
      sortable: true,
    },
    {
      name: "Merchant Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Email Address",
      selector: (row) => row.email,
      sortable: true,
      grow: 2,
    },
    {
      name: "Code",
      selector: (row) => row.code,
      sortable: true,
    },
    {
      name: "Migrated",
      selector: (row) => (row.migrated ? "true" : "false"),
      sortable: true,
    },
    {
      name: "Terminal ID",
      selector: (row) => row.terminalId,
      sortable: true,
    },
     {
      name: "Retailer ID",
      selector: (row) => row.retailerId,
      sortable: true,
    },
  ];

  const pendingColumns = [
    {
      name: "Onboarding ID",
      selector: (row) => row.onboardingId,
      sortable: true,
    },
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Email Address",
      selector: (row) => row.email,
      sortable: true,
      grow: 3,
    },
    {
      name: "Created",
      selector: (row) => row.created ? new Date(row.created).toLocaleDateString() : "N/A",
      sortable: true,
    },
    {
      name: "Level",
      selector: (row) => row.approvalLevel,
      sortable: true,
      width: "80px"
    },
    {
      name: "Type",
      selector: (row) => row.businessType,
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <button
          className="bg-primary text-white px-3 py-1 rounded-md text-sm font-semibold hover:bg-opacity-80 transition-all"
          onClick={() => {
            setSelectedProspect(row);
            setOpenUpdateModal(true);
          }}
        >
          Update User
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "150px"
    },
  ];

  const customStyles = {
    rows: {
      style: {
        cursor: "pointer", // Change cursor to pointer
      },
    },
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredData = users.filter((item) => {
    //item.name.toLowerCase().includes(searchTerm.toLowerCase())
  const term = searchTerm.toLowerCase();

  return (
    item.name?.toLowerCase().includes(term) ||
    item.id?.toString().toLowerCase().includes(term) ||
    item.terminalId?.toString().toLowerCase().includes(term) ||
    item.retailerId?.toString().toLowerCase().includes(term)
  );
});

  const handlePendingRowSelected = (row) => {
    navigate(`/merchants/pending/${row.onboardingId}`, { state: { prospect: row } });
  };

  const filteredPendingData = pendingProspects.filter((item) => {
    const term = pendingSearchTerm.toLowerCase();
    return (
      item.name?.toLowerCase().includes(term) ||
      item.email?.toLowerCase().includes(term) ||
      item.merchantName?.toLowerCase().includes(term)
    );
  });

  const subHeaderComponentMemo = React.useMemo(() => {
    const handleClear = () => {
      if (activeTab === "merchants") {
        if (searchTerm) {
          setResetPaginationToggle(!resetPaginationToggle);
          setSearchTerm("");
        }
      } else {
        setPendingSearchTerm("");
      }
    };

    return (
      <FilterComponent
        onFilterName={(e) => activeTab === "merchants" ? handleSearch(e) : setPendingSearchTerm(e.target.value)}
        onClear={handleClear}
        filterText={activeTab === "merchants" ? searchTerm : pendingSearchTerm}
      />
    );
  }, [searchTerm, resetPaginationToggle, activeTab, pendingSearchTerm]);

  const handleRowSelected = (row) => {
    if(userrole.role === "Business Support Checker" || userrole.role === "Risk User") {
      return;
    }
    navigate(`/merchants/${row.id}`);
  };

  return (
    <div className="h-screen">
      <div className="flex justify-between items-center py-4">
        <h1 className="text-navy-blue text-2xl font-bold">
          Merchant Management
        </h1>
        <div>
          <ProfileNav />
        </div>
      </div>
      {/* <div className="flex items-center justify-evenly pt-4">
        <div className="relative">
          <input
            type="text"
            placeholder="search"
            className="w-[402px] h-[40px] rounded-[10px] px-2 border border-gray-300 focus:outline-none"
          />
          <span>
            <svg
              className="absolute right-8 top-3"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g opacity="0.5">
                <path
                  d="M7.22235 13.4445C10.6589 13.4445 13.4447 10.6587 13.4447 7.22223C13.4447 3.78579 10.6589 1 7.22235 1C3.78584 1 1 3.78579 1 7.22223C1 10.6587 3.78584 13.4445 7.22235 13.4445Z"
                  stroke="#030229"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15.0001 15.0005L11.6167 11.6172"
                  stroke="#030229"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
            </svg>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="start-date">Start Date</label>
          <div className="bg-white w-[130px] h-[40px] rounded-[5px] flex items-center justify-between px-2">
             <span className="font-semibold text-sm text-gray-400">Start Date</span> 
            <input type="date" id="start-date" className="focus:outline-none" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="end-date">End Date</label>
          <div className="bg-white w-[130px] h-[40px] rounded-[5px] flex items-center justify-between px-2">
            <input type="date" id="end-date" className="focus:outline-none" />
          </div>
        </div>

        <div className="relative">
          <button className="text-white bg-text-orange w-[165px] h-[42px] rounded-[10px]">
            Export
          </button>
          <span>
            <svg
              className="absolute left-10 top-[18px]"
              width="10"
              height="10"
              viewBox="0 0 10 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clip-path="url(#clip0_22_2583)">
                <path
                  d="M6.91242 3.75H6.24992V1.66667C6.24992 1.4375 6.06242 1.25 5.83325 1.25H4.16659C3.93742 1.25 3.74992 1.4375 3.74992 1.66667V3.75H3.08742C2.71659 3.75 2.52909 4.2 2.79159 4.4625L4.70409 6.375C4.86659 6.5375 5.12909 6.5375 5.29159 6.375L7.20409 4.4625C7.46659 4.2 7.28325 3.75 6.91242 3.75ZM2.08325 7.91667C2.08325 8.14583 2.27075 8.33333 2.49992 8.33333H7.49992C7.72909 8.33333 7.91659 8.14583 7.91659 7.91667C7.91659 7.6875 7.72909 7.5 7.49992 7.5H2.49992C2.27075 7.5 2.08325 7.6875 2.08325 7.91667Z"
                  fill="white"
                />
              </g>
              <defs>
                <clipPath id="clip0_22_2583">
                  <rect width="10" height="10" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </span>
        </div>
      </div> */}
      {/* <div className="flex items-center justify-end">
        <button
          className="w-[280px] h-[50px] bg-black text-sm text-white rounded-md cursor-pointer"
          type="button"
          onClick={migrateUnregisteredMerchants}
        >
          {loading ? <Spinner /> : "Migrate to Payloft"}
        </button>
      </div> */}
      <div className="bg-white w-full rounded-md mt-16">
        <div className="flex items-center justify-end gap-3 px-8">
          <p className="border-r pr-5 font-normal text-[12px]">1 Selected</p>
          <span>
            <svg
              width="28"
              height="28"
              viewBox="0 0 28 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clip-path="url(#clip0_3367_2872)">
                <path
                  d="M20.3333 14.3333V18.5C20.3333 18.9583 19.9583 19.3333 19.5 19.3333H9.5C9.04167 19.3333 8.66667 18.9583 8.66667 18.5V14.3333C8.66667 13.875 8.29167 13.5 7.83333 13.5C7.375 13.5 7 13.875 7 14.3333V19.3333C7 20.25 7.75 21 8.66667 21H20.3333C21.25 21 22 20.25 22 19.3333V14.3333C22 13.875 21.625 13.5 21.1667 13.5C20.7083 13.5 20.3333 13.875 20.3333 14.3333ZM15.3333 14.0583L16.9 12.4917C17.225 12.1667 17.75 12.1667 18.075 12.4917C18.4 12.8167 18.4 13.3417 18.075 13.6667L15.0833 16.6583C14.7583 16.9833 14.2333 16.9833 13.9083 16.6583L10.9167 13.6667C10.5917 13.3417 10.5917 12.8167 10.9167 12.4917C11.2417 12.1667 11.7667 12.1667 12.0917 12.4917L13.6667 14.0583V6.83333C13.6667 6.375 14.0417 6 14.5 6C14.9583 6 15.3333 6.375 15.3333 6.83333V14.0583Z"
                  fill="#323232"
                />
              </g>
              <defs>
                <clipPath id="clip0_3367_2872">
                  <rect width="28" height="28" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </span>
          <span>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 8.75C5.69036 8.75 6.25 9.30964 6.25 10C6.25 10.6904 5.69036 11.25 5 11.25C4.30964 11.25 3.75 10.6904 3.75 10C3.75 9.30964 4.30964 8.75 5 8.75Z"
                fill="#1C1C1C"
              />
              <path
                d="M10 8.75C10.6904 8.75 11.25 9.30964 11.25 10C11.25 10.6904 10.6904 11.25 10 11.25C9.30964 11.25 8.75 10.6904 8.75 10C8.75 9.30964 9.30964 8.75 10 8.75Z"
                fill="#1C1C1C"
              />
              <path
                d="M16.25 10C16.25 9.30964 15.6904 8.75 15 8.75C14.3096 8.75 13.75 9.30964 13.75 10C13.75 10.6904 14.3096 11.25 15 11.25C15.6904 11.25 16.25 10.6904 16.25 10Z"
                fill="#1C1C1C"
              />
            </svg>
          </span>
        </div>
        <div className="flex items-center justify-between px-3 gap-2">
          <div className="flex items-center gap-4">
            <p 
              className={`cursor-pointer pb-2 text-sm font-semibold transition-all ${activeTab === "merchants" ? "border-b-2 border-text-orange text-text-orange" : "text-gray-500 hover:text-black"}`}
              onClick={() => setActiveTab("merchants")}
            >
              Merchants
            </p>
            <p 
              className={`cursor-pointer pb-2 text-sm font-semibold transition-all ${activeTab === "pending" ? "border-b-2 border-text-orange text-text-orange" : "text-gray-500 hover:text-black"}`}
              onClick={() => setActiveTab("pending")}
            >
              Pending Requests
            </p>
          </div>

          <div className="flex items-center gap-2">
            {activeTab === "merchants" && (
              <>
                <div className="flex items-center gap-2 border-r pr-5">
                  <p className="text-[12px] font-normal">All Merchants</p>
                  <span>
                    <svg
                      width="12"
                      height="6"
                      viewBox="0 0 12 6"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1.5 0.75L6 5.25L10.5 0.75"
                        stroke="#464255"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="bg-text-orange w-[6px] h-[6px] rounded-full"></div>
                  <p className="text-text-orange text-[12px] font-bold">
                    All Merchant
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="bg-black w-[6px] h-[6px] rounded-full"></div>
                  <p className="text-[12px] font-normal">Migrated</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="bg-red-400 w-[6px] h-[6px] rounded-full"></div>
                  <p className="text-[12px] font-normal">Unmigrated</p>
                </div>
              </>
            )}
          </div>

          {/* <Pagination
            totalItems={filteredUsers.length}
            itemsPerPage={itemsPerPage}
            paginate={handlePagination}
            currentPage={currentPage}
          /> */}
          {/* <div className="flex items-center w-[268px] justify-evenly">
              <p className="bg-black-shade/5 py-2 px-4 text-[12px]">1</p>

              <p className="text-[12px]">2</p>
              <p className="text-[12px]">3</p>
              <p className="text-[12px]">4</p>
              <p className="text-[12px]">5</p>
            </div> */}
        </div>
        <DataTableBase
          columns={activeTab === "merchants" ? columns : pendingColumns}
          data={activeTab === "merchants" ? filteredData : filteredPendingData}
          progressPending={activeTab === "merchants" ? loading : pendingLoading}
          subHeaderComponent={subHeaderComponentMemo}
          onRowClicked={activeTab === "merchants" ? handleRowSelected : handlePendingRowSelected}
          pagination
          selectableRows
          persistTableHead
          highlightOnHover // Optional: adds hover effect
          customStyles={customStyles}
        />
      </div>

      {openUpdateModal && (
        <UpdateProspectModal
          prospect={selectedProspect}
          setOpenModal={setOpenUpdateModal}
          onSuccess={fetchPendingRequests}
          userEmail={user?.email || "Admin"}
        />
      )}

      <style>
        {`
        /* Customize the label (the container) */
.container {
  display: block;
  position: relative;
  padding-left: 35px;
  cursor: pointer;
  font-size: 12px;
  color: rgba(28, 28, 28, 0.4);
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

/* Hide the browser's default checkbox */
.container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

/* Create a custom checkbox */
.checkmark {
  position: absolute;
  top: 0;
  left: 0;
  height: 13.5px;
  width: 13.5px;
  background-color: #eee;
  border-radius: 3px;
  margin-top:2px;
}

/* On mouse-over, add a grey background color */
.container:hover input ~ .checkmark {
  background-color: #ccc;
}

/* When the checkbox is checked, add a blue background */
.container input:checked ~ .checkmark {
  background-color: #323232;
}

/* Create the checkmark/indicator (hidden when not checked) */
.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

/* Show the checkmark when checked */
.container input:checked ~ .checkmark:after {
  display: block;
}

/* Style the checkmark/indicator */
.container .checkmark:after {
  left: 6px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  -webkit-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  transform: rotate(45deg);
}
        `}
      </style>
    </div>
  );
};

export default Merchant;
